import { Fix64 } from "../maths/fix64";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { hitTouches } from "./petKit";
import { applyPetHit, petCanReach, strongestInReach } from "./petStatus";
import type { PetSystem } from "./petSystem";
import { withinBark } from "./troopRules";

/**
 * A Pets v3 pulse (docs/PETS.md "Pets v3": Lantern Flare, Fox Fire, Crystal Ward, Tide Pool, Glint): due every
 * `everyTicks` of running waves, it goes off once it has someone to work on (raiders in reach for its hit, fortress
 * troops for its shield or heal; a due pulse waits, so it is never wasted on an empty flag). Raiders within `reach`
 * of the flag (flyers when `air`; only the one with most health when `strongest`) take its hit; fortress troops there
 * get a shield of `troopShieldPercent` of their max HP for `shieldTicks` and heal `troopHealPercent`. PetBarked
 * (a = raiders hit, b = the longest status it set in ticks, c = troops helped). Shields run down here too.
 */
export function tickPulse(sim: LevelSim, pet: PetSystem): void {
  const p = pet.rule.pulse;
  for (const t of sim.troops.slots) if (t.shieldTicks > 0 && --t.shieldTicks === 0) t.shield = Fix64.Zero;
  if (!p) return;
  if (pet.pulseTimer > 0) pet.pulseTimer--;
  if (pet.pulseTimer > 0) return;
  const r = sim.troops.rally, fx = Fix64.fromInt(r.x), fy = Fix64.fromInt(r.y), r2 = p.reach.mul(p.reach), buff = pet.pulseBuff!;
  const hits = hitTouches(p.hit), helps = p.troopShieldPercent > 0 || p.troopHealPercent > 0;
  let raiders = 0, troops = 0;
  if (hits) for (const e of sim.enemySlots) if (petCanReach(sim, e, p.air, fx, fy, r2)) raiders++;
  if (helps) for (const t of sim.troops.slots) if (t.alive && withinBark(buff, r.x, r.y, t.x, t.y)) troops++;
  if (raiders === 0 && troops === 0) return;
  pet.pulseTimer = p.everyTicks;
  let hit = 0;
  if (raiders > 0) {
    if (p.strongest) { const e = strongestInReach(sim, p.air, fx, fy, r2); if (e !== null) { applyPetHit(sim, e, p.hit); hit = 1; } }
    else {
      const list: Enemy[] = pet.scratch;
      list.length = 0;
      for (const e of sim.enemySlots) if (petCanReach(sim, e, p.air, fx, fy, r2)) list.push(e);
      for (const e of list) applyPetHit(sim, e, p.hit);
      hit = list.length;
      list.length = 0;
    }
  }
  for (const t of sim.troops.slots) {
    if (troops === 0 || !t.alive || !withinBark(buff, r.x, r.y, t.x, t.y)) continue;
    if (p.troopShieldPercent > 0) { t.shield = t.maxHp.mulInt(p.troopShieldPercent).divInt(PERCENT); t.shieldTicks = p.shieldTicks; }
    if (p.troopHealPercent > 0) t.hp = Fix64.min(t.maxHp, t.hp.add(t.maxHp.mulInt(p.troopHealPercent).divInt(PERCENT)));
  }
  sim.events.add(SimEventKind.PetBarked, hit, Math.max(p.shieldTicks, p.hit.markTicks, p.hit.slowTicks, p.hit.burnTicks), troops);
}

const PERCENT = 100;
